import React, { createContext, useContext, useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../components/Icon';
import type { IconName } from '../components/Icon';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

/**
 * If a DOM node with this id exists, success/info toasts are portaled into it
 * (sidebar mode: full width of host, stack grows upward as new toasts append).
 * Otherwise they fall back to the fixed top-right corner.
 */
const TOAST_SLOT_ID = 'bp-toast-slot';

/**
 * A registered modal anchor: a DOM element that lives between a ModalShell's
 * header and body. `error`/`warning` toasts portal into the TOP-most modal's
 * anchor (highest z-index, ties broken by most-recent registration) so an
 * error appears inside the modal the user is acting on, right under the
 * action button. `zTier` is the numeric z-index of the owning modal; `seq` is
 * a monotonic registration counter for tie-breaking.
 */
interface ModalAnchor {
  el: HTMLElement;
  zTier: number;
  seq: number;
}

interface Toast {
  id: string;
  type: ToastType;
  message: string;
  duration: number;
  /** Raw technical text folded under «Ver detalle técnico». */
  detail: string | null;
}

/**
 * Second argument of every `toast.*` call. A bare number is the legacy form
 * (the duration in ms) and keeps working unchanged.
 *
 * `detail` is the ORIGINAL technical text of an error (the backend's raw
 * message, a provider's answer, «Failed to fetch»…). The toast shows the
 * translated `message` and folds `detail` under «Ver detalle técnico», so the
 * user reads something they understand and can still paste the raw text to
 * support. Empty/blank → no fold.
 */
export interface ToastOptions {
  duration?: number;
  detail?: string | null;
}

export type ToastFn = (message: string, options?: number | ToastOptions) => void;

/** Texts the provider paints itself. Spanish by default (the admin is
 *  Spanish-only); the tenant feeds its `t()` through {@link useToastLabels}. */
export interface ToastLabels {
  showDetail: string;
  hideDetail: string;
  close: string;
}

const DEFAULT_LABELS: ToastLabels = {
  showDetail: 'Ver detalle técnico',
  hideDetail: 'Ocultar detalle técnico',
  close: 'Cerrar',
};

/** Why a toast's auto-dismiss timer is held: the pointer is over it, or its
 *  technical detail is unfolded (someone is reading or copying it). */
type HoldReason = 'hover' | 'open';

interface ToastTimer {
  timer: ReturnType<typeof setTimeout> | null;
  /** ms still to run before auto-dismiss. */
  remaining: number;
  /** When the current run started (only meaningful while `timer` is set). */
  startedAt: number;
  holds: Set<HoldReason>;
}

/** Visual placement of a rendered toast. */
type ToastVariant = 'slot' | 'corner' | 'modal' | 'center';

interface ToastContextValue {
  toast: {
    success: ToastFn;
    error: ToastFn;
    warning: ToastFn;
    info: ToastFn;
  };
  /**
   * Registers a modal anchor element so error/warning toasts render inside the
   * currently-open modal. Returns an unregister callback (call on close/unmount).
   * Consumed by ModalShell via {@link useModalToastAnchor}.
   */
  registerModalAnchor: (el: HTMLElement, zTier: number) => () => void;
  /** Overrides the provider's own texts (see {@link useToastLabels}). */
  setLabels: (labels: Partial<ToastLabels> | null) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const MAX_TOASTS = 5;
const DEFAULT_DURATION = 5000;
/** A toast that carries a technical detail lives longer by default: the user
 *  has a message to read AND a fold to open. Hovering or unfolding it holds the
 *  timer anyway, so this only covers someone who reads without touching. */
const DETAIL_DURATION = 12000;

const TOAST_ICONS: Record<ToastType, IconName> = {
  success: 'check_circle',
  error: 'error',
  warning: 'warning',
  info: 'info',
};

const TOAST_COLORS: Record<ToastType, string> = {
  success: 'bg-emerald-50 dark:bg-emerald-900/90 border-emerald-200 dark:border-emerald-700 text-emerald-800 dark:text-emerald-200',
  error: 'bg-red-50 dark:bg-red-900/90 border-red-200 dark:border-red-700 text-red-800 dark:text-red-200',
  warning: 'bg-amber-50 dark:bg-amber-900/90 border-amber-200 dark:border-amber-700 text-amber-800 dark:text-amber-200',
  info: 'bg-blue-50 dark:bg-blue-900/90 border-blue-200 dark:border-blue-700 text-blue-800 dark:text-blue-200',
};

/** error + warning render near the action (modal band or prominent top-center). */
function isError(type: ToastType): boolean {
  return type === 'error' || type === 'warning';
}

interface ToastItemProps {
  toast: Toast;
  variant: ToastVariant;
  labels: ToastLabels;
  onDismiss: (id: string) => void;
  onHold: (id: string, reason: HoldReason) => void;
  onRelease: (id: string, reason: HoldReason) => void;
}

/** One rendered toast. Owns the fold state of its technical detail; while the
 *  fold is open (or the pointer is over it) the provider's countdown is held. */
function ToastItem({ toast: t, variant, labels, onDismiss, onHold, onRelease }: ToastItemProps) {
  const [open, setOpen] = useState(false);
  const slotMode = variant === 'slot';
  const wrapper =
    variant === 'slot'
      ? 'w-full px-3 py-2'
      : variant === 'modal'
      ? 'w-full px-4 py-3'
      : variant === 'center'
      ? 'w-full max-w-md px-4 py-3'
      : 'max-w-sm px-4 py-3'; // corner

  // A toast can re-mount when it moves (modal opened/closed): its fold starts
  // closed again, so drop any hold this instance placed.
  useEffect(() => () => {
    onRelease(t.id, 'open');
    onRelease(t.id, 'hover');
  }, [t.id, onRelease]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) onHold(t.id, 'open');
    else onRelease(t.id, 'open');
  };

  return (
    <div
      className={`flex items-start gap-2 rounded-xl shadow-lg border backdrop-blur-sm animate-in duration-300 ${wrapper} ${TOAST_COLORS[t.type]}`}
      role={isError(t.type) ? 'alert' : 'status'}
      onMouseEnter={() => onHold(t.id, 'hover')}
      onMouseLeave={() => onRelease(t.id, 'hover')}
    >
      <Icon name={TOAST_ICONS[t.type]} className={`flex-shrink-0 ${slotMode ? '!text-[16px] mt-0.5' : '!text-[20px]'}`} />
      <div className="flex-1 min-w-0">
        <p className={`font-medium break-words ${slotMode ? 'text-[11px] leading-snug' : 'text-sm'}`}>
          {t.message}
        </p>
        {t.detail && (
          <div className="mt-1">
            <button
              type="button"
              onClick={toggle}
              aria-expanded={open}
              className={`inline-flex items-center gap-0.5 rounded opacity-80 hover:opacity-100 hover:underline ${slotMode ? 'text-[10px]' : 'text-xs'}`}
            >
              <Icon name={open ? 'expand_less' : 'expand_more'} className="!text-[14px]" />
              {open ? labels.hideDetail : labels.showDetail}
            </button>
            {open && (
              <p className="mt-1 max-h-40 overflow-y-auto select-all break-all rounded bg-black/5 px-2 py-1 font-mono text-[11px] leading-snug opacity-90 dark:bg-white/5">
                {t.detail}
              </p>
            )}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={() => onDismiss(t.id)}
        className="p-1 rounded-md hover:bg-black/10 transition-colors flex-shrink-0"
        aria-label={labels.close}
      >
        <Icon name="close" className={`${slotMode ? '!text-[14px]' : '!text-[16px]'}`} />
      </button>
    </div>
  );
}

/** Later sources win, but only with a real text — an `undefined` or blank
 *  label never blanks the Spanish default. */
function mergeLabels(base: ToastLabels, ...overrides: Array<Partial<ToastLabels> | null | undefined>): ToastLabels {
  const out: ToastLabels = { ...base };
  for (const o of overrides) {
    if (!o) continue;
    (Object.keys(out) as Array<keyof ToastLabels>).forEach((k) => {
      const v = o[k];
      if (typeof v === 'string' && v.trim()) out[k] = v;
    });
  }
  return out;
}

function normalizeOptions(options: number | ToastOptions | undefined): { duration?: number; detail: string | null } {
  if (typeof options === 'number') return { duration: options, detail: null };
  const detail = (options?.detail ?? '').trim();
  return { duration: options?.duration, detail: detail || null };
}

export function ToastProvider({
  children,
  labels: labelsProp,
}: {
  children: React.ReactNode;
  /** Texts the provider paints (fold + close). Spanish by default. */
  labels?: Partial<ToastLabels>;
}) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timersRef = useRef<Map<string, ToastTimer>>(new Map());
  const [labelsOverride, setLabelsOverride] = useState<Partial<ToastLabels> | null>(null);
  const labels = mergeLabels(DEFAULT_LABELS, labelsProp, labelsOverride);
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  const [anchors, setAnchors] = useState<ModalAnchor[]>([]);
  const seqRef = useRef(0);

  // The sidebar slot lives deep in the app tree (inside the Sidebar), so it only
  // mounts after children render. Observe document.body so we pick it up when it
  // appears (e.g. login → dashboard) and drop it if it unmounts.
  //
  // Both layouts render the sidebar TWICE — a desktop instance (`hidden md:flex`)
  // and a mobile drawer (`md:hidden`) — so two same-id `#bp-toast-slot` nodes
  // coexist. `getElementById` would always return the first (desktop) one, which
  // is `display:none` on mobile and would hide success/info toasts there. Resolve
  // to the *visible, on-screen* slot instead; when none is visible (mobile, drawer
  // closed) leave `slot` null so toasts use the fixed corner fallback.
  useEffect(() => {
    const isVisible = (el: HTMLElement): boolean => {
      const host = el.parentElement; // the slot's wrapper (not empty:hidden)
      if (!host || host.offsetParent === null) return false; // display:none ancestor
      const r = host.getBoundingClientRect();
      return r.width > 0 && r.right > 0 && r.left < window.innerWidth; // on-screen
    };
    const update = () => {
      const candidates = Array.from(document.querySelectorAll<HTMLElement>(`#${TOAST_SLOT_ID}`));
      const next = candidates.find(isVisible) ?? null;
      setSlot((prev) => (prev === next ? prev : next));
    };
    update();
    const obs = new MutationObserver(update);
    obs.observe(document.body, { childList: true, subtree: true });
    // Crossing the md breakpoint (resize/rotate) swaps which slot is visible;
    // MutationObserver(childList) won't fire on resize, so listen explicitly.
    window.addEventListener('resize', update);
    return () => {
      obs.disconnect();
      window.removeEventListener('resize', update);
    };
  }, []);

  const clearTimer = useCallback((id: string) => {
    const entry = timersRef.current.get(id);
    if (entry?.timer) clearTimeout(entry.timer);
    timersRef.current.delete(id);
  }, []);

  const dismiss = useCallback((id: string) => {
    clearTimer(id);
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, [clearTimer]);

  const runTimer = useCallback((id: string) => {
    const entry = timersRef.current.get(id);
    if (!entry || entry.timer || entry.holds.size > 0) return;
    entry.startedAt = Date.now();
    entry.timer = setTimeout(() => dismiss(id), Math.max(entry.remaining, 0));
  }, [dismiss]);

  /** Stops the countdown while `reason` holds (hover, fold open). */
  const hold = useCallback((id: string, reason: HoldReason) => {
    const entry = timersRef.current.get(id);
    if (!entry) return;
    entry.holds.add(reason);
    if (entry.timer) {
      clearTimeout(entry.timer);
      entry.timer = null;
      entry.remaining -= Date.now() - entry.startedAt;
      // Whatever was left, never resume into an instant disappearance.
      if (entry.remaining < 1500) entry.remaining = 1500;
    }
  }, []);

  const release = useCallback((id: string, reason: HoldReason) => {
    const entry = timersRef.current.get(id);
    if (!entry) return;
    entry.holds.delete(reason);
    runTimer(id);
  }, [runTimer]);

  const addToast = useCallback(
    (type: ToastType, message: string, options?: number | ToastOptions) => {
      const { duration: requested, detail } = normalizeOptions(options);
      const duration = requested ?? (detail ? DETAIL_DURATION : DEFAULT_DURATION);
      const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const toast: Toast = { id, type, message, duration, detail };

      setToasts((prev) => {
        const next = [...prev, toast];
        if (next.length > MAX_TOASTS) {
          const removed = next.shift();
          if (removed) clearTimer(removed.id);
        }
        return next;
      });

      // Every toast auto-dismisses after `duration` (default 5s, 12s with a
      // detail) — including error/warning — unless held: the pointer over it
      // or its technical detail unfolded pause the countdown. They can still be
      // closed early (X), by the hosting modal closing, or by MAX_TOASTS eviction.
      timersRef.current.set(id, { timer: null, remaining: duration, startedAt: 0, holds: new Set() });
      runTimer(id);
    },
    [clearTimer, runTimer]
  );

  const registerModalAnchor = useCallback((el: HTMLElement, zTier: number) => {
    const seq = seqRef.current++;
    setAnchors((prev) => [...prev, { el, zTier, seq }]);
    return () => {
      setAnchors((prev) => prev.filter((a) => a.el !== el));
    };
  }, []);

  // When the last modal closes (anchor stack empties), clear any lingering
  // error/warning toasts that were showing inside it. This is what makes
  // "close the modal" (or a successful retry that closes it) dismiss the error.
  const prevAnchorCount = useRef(0);
  useEffect(() => {
    if (prevAnchorCount.current > 0 && anchors.length === 0) {
      setToasts((prev) => {
        prev.filter((t) => isError(t.type)).forEach((t) => clearTimer(t.id));
        return prev.filter((t) => !isError(t.type));
      });
    }
    prevAnchorCount.current = anchors.length;
  }, [anchors.length, clearTimer]);

  const toast = useMemo(
    () => ({
      success: (message: string, options?: number | ToastOptions) => addToast('success', message, options),
      error: (message: string, options?: number | ToastOptions) => addToast('error', message, options),
      warning: (message: string, options?: number | ToastOptions) => addToast('warning', message, options),
      info: (message: string, options?: number | ToastOptions) => addToast('info', message, options),
    }),
    [addToast]
  );

  // The top-most modal owns error display: highest z-index wins, ties broken by
  // most-recent registration. Insertion order alone is NOT visual order (React
  // effect ordering ≠ z-stacking), so we rank explicitly.
  const topAnchor = anchors.length
    ? anchors.reduce((best, a) =>
        a.zTier > best.zTier || (a.zTier === best.zTier && a.seq > best.seq) ? a : best
      )
    : null;

  const errorToasts = toasts.filter((t) => isError(t.type));
  const infoToasts = toasts.filter((t) => !isError(t.type));

  const renderToast = (t: Toast, variant: ToastVariant) => (
    <ToastItem
      key={t.id}
      toast={t}
      variant={variant}
      labels={labels}
      onDismiss={dismiss}
      onHold={hold}
      onRelease={release}
    />
  );

  const setLabels = useCallback((next: Partial<ToastLabels> | null) => setLabelsOverride(next), []);

  return (
    <ToastContext.Provider value={{ toast, registerModalAnchor, setLabels }}>
      {children}

      {/* success / info — ambient: sidebar slot when present, else top-right corner */}
      {slot
        ? createPortal(<>{infoToasts.map((t) => renderToast(t, 'slot'))}</>, slot)
        : (
          <div className="fixed top-4 right-4 z-[110] flex flex-col gap-3 items-end">
            {infoToasts.map((t) => renderToast(t, 'corner'))}
          </div>
        )}

      {/* error / warning — near the action: inside the top-most modal, else prominent top-center */}
      {topAnchor
        ? createPortal(<>{errorToasts.map((t) => renderToast(t, 'modal'))}</>, topAnchor.el)
        : errorToasts.length > 0
        ? (
          <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[10050] flex flex-col gap-3 items-center w-full max-w-md px-4 pointer-events-none">
            <div className="flex flex-col gap-3 w-full pointer-events-auto">
              {errorToasts.map((t) => renderToast(t, 'center'))}
            </div>
          </div>
        )
        : null}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return ctx;
}

/**
 * Non-throwing accessor for the modal-anchor registration function. Returns
 * `null` when there is no ToastProvider ancestor (mirrors `useConfirmOptional`),
 * so ModalShell can render in isolation without crashing. When present, the
 * returned fn registers a modal anchor for in-modal error display.
 */
export function useModalToastAnchor(): ToastContextValue['registerModalAnchor'] | null {
  const ctx = useContext(ToastContext);
  return ctx ? ctx.registerModalAnchor : null;
}

/**
 * Replaces the provider's own texts (the «Ver detalle técnico» fold and the
 * close button's label) while the calling component is mounted. For an app
 * whose `ToastProvider` sits ABOVE its translation provider (the tenant): a
 * tiny component under the locale provider calls this with its `t()`.
 * No-op without a ToastProvider.
 */
export function useToastLabels(labels: Partial<ToastLabels>): void {
  const ctx = useContext(ToastContext);
  const setLabels = ctx?.setLabels;
  const { showDetail, hideDetail, close } = labels;
  useEffect(() => {
    if (!setLabels) return;
    setLabels({ showDetail, hideDetail, close });
    return () => setLabels(null);
  }, [setLabels, showDetail, hideDetail, close]);
}
